import { Link, linkOptions } from '@tanstack/react-router';
import { useLegalPages } from '@/hook/useLegalPages';
import { FeedbackLink } from './FeedbackLink';
import { MonoLabel } from './MonoLabel';

// Internal links route; the rest are text until the parent brand supplies URLs (#46).
const COLUMNS = [
  {
    heading: 'Aeon Karaoke',
    links: [
      { label: 'Library', link: linkOptions({ to: '/library' }) },
      { label: 'Billboard', link: linkOptions({ to: '/billboard' }) },
      { label: 'Shop', link: linkOptions({ to: '/shop' }) },
      { label: 'Plans', link: linkOptions({ to: '/settings/$tab', params: { tab: 'plan' } }) },
    ],
  },
  { heading: 'Company', links: ['Aeon Entertainment', 'About', 'Careers', 'Press'] },
  { heading: 'Follow', links: ['Instagram', 'TikTok', 'YouTube', 'X'] },
  { heading: 'Help', links: ['Support', 'Contact', 'Status', 'Licensing'] },
] as const;

/** The shell's footer. Privacy and Terms link to the documents on the landing
 * domain and appear only once the deployment names them; Send feedback heads
 * the Help column once it names a form. */
export function Footer() {
  const legal = useLegalPages();
  return (
    <footer className="mt-10 border-t border-line">
      <div className="mx-auto grid max-w-page grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-10 px-10 pb-14 pt-12">
        <div>
          <div className="flex items-center gap-[10px]">
            <img src="/logo.svg" alt="" width={22} height={22} />
            <span className="text-[15px] font-semibold uppercase tracking-[0.12em]">Aeon</span>
          </div>
          <p className="mt-4 max-w-[30ch] text-[13px] leading-[1.6] text-faint">
            Aeon Karaoke is a product of Aeon Entertainment, Inc.
          </p>
        </div>
        {COLUMNS.map((column) => (
          <div key={column.heading}>
            <MonoLabel size="md" className="block">
              {column.heading}
            </MonoLabel>
            <div className="mt-4 flex flex-col gap-[11px]">
              {column.heading === 'Help' && (
                <FeedbackLink className="text-sm text-mute hover:text-acc">Send feedback</FeedbackLink>
              )}
              {column.links.map((link) =>
                typeof link === 'string' ? (
                  <span key={link} className="text-sm text-mute">
                    {link}
                  </span>
                ) : (
                  <Link key={link.label} {...link.link} className="text-sm text-mute hover:text-acc">
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mx-auto max-w-page px-10 pb-11">
        <div className="flex flex-wrap items-baseline justify-between gap-5 border-t border-stripeA pt-6">
          <span className="font-mono text-[11px] tracking-[0.1em] text-faint">
            © 2026 Aeon Entertainment, Inc. All rights reserved.
          </span>
          {legal && (
            <div className="flex flex-wrap gap-5 text-[13px]">
              <a href={legal.privacy} target="_blank" rel="noreferrer" className="text-mute hover:text-acc">
                Privacy
              </a>
              <a href={legal.terms} target="_blank" rel="noreferrer" className="text-mute hover:text-acc">
                Terms
              </a>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
